import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  AccountsService,
  LoadingService,
  PopoverService,
  UserdataService,
  WorkScheduleService
} from '@services';
import { WorkScheduleEntryType, WorkScheduleType } from '@services/work-schedule/model';
import { EntryModalComponent } from '@modules/management/pages/details/work-schedule/modals/entry-modal/entry-modal.component';
import { ScheduleCalendarComponent } from '@modules/shared/components/schedule-calendar/schedule-calendar.component';

@Component({
  selector: 'app-my-schedule',
  templateUrl: './my-schedule.page.html',
  styleUrls: ['./my-schedule.page.scss'],
})
export class MySchedulePage implements OnInit {

  @ViewChild('scheduleCalendar') scheduleCalendar: ScheduleCalendarComponent;
  @Input() public isModal: boolean = false;
  @Input() public userID: number = 0;
  @Input() public locationID: number = 0;
  public title: string = 'WORK_SCHEDULE.My_Schedule';

  // Worker info
  // Schedule IDs (from subscriber defaults)
  public workDaysScheduleID: number = 0;
  public daysOffScheduleID: number = 0;

  // Worker's personal time off entries (separated into upcoming and completed)
  public upcomingTimeOff: any[] = [];
  public completedTimeOff: any[] = [];

  // Loading state
  public isLoading: boolean = true;

  // For delete confirmation
  private entryToDelete: any = null;

  public daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  public daysOfWeekShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  private readonly workerEntryTypes: WorkScheduleEntryType[] = [
    WorkScheduleEntryType.Day_Of_Week,
    WorkScheduleEntryType.Specific_Date,
    WorkScheduleEntryType.Relative_Date
  ];

  constructor(
    private location: Location,
    private translate: TranslateService,
    private loadingService: LoadingService,
    private accountsService: AccountsService,
    private userDataService: UserdataService,
    private workScheduleService: WorkScheduleService,
    private modalController: ModalController,
    private popoverService: PopoverService
  ) {}

  async ngOnInit() {
    const currentUserID = +this.userDataService.userID;
    this.title = this.userID && this.userID !== currentUserID
      ? 'SHARED.User_Work_Schedules'
      : 'WORK_SCHEDULE.My_Schedule';
    this.userID = this.userID || currentUserID;
    this.locationID = this.locationID || this.accountsService.getUserLocation(this.userID) || 0;

    await this.loadScheduleInfo();
  }

  public back(): void {
    this.location.back();
  }

  private async loadScheduleInfo(): Promise<void> {
    this.isLoading = true;

    try {
      // Ensure work schedules are loaded
      await this.workScheduleService.refreshWorkSchedules();

      // Pass 0 for schedule IDs - the backend will auto-determine the effective
      // schedule based on the worker's inheritance chain:
      // 1. Primary team's schedule config
      // 2. Primary team's location default (if single location)
      // 3. Worker's location default (if single location)
      // 4. Subscriber default
      this.workDaysScheduleID = 0;
      this.daysOffScheduleID = 0;


      // Load worker's personal days off entries
      await this.loadWorkerEntries();
    } catch (error) {
      console.error('Error loading schedule info:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadWorkerEntries(): Promise<void> {
    try {
      const entries = await this.workScheduleService.getSubjectEntries(
        'worker',
        this.userID,
        WorkScheduleType.Days_Off
      );
      // Group entries by entryID for display
      const allEntries = this.groupEntriesByID(entries || []);

      // Separate into upcoming and completed
      this.categorizeEntries(allEntries);
    } catch (error) {
      console.error('Error loading worker entries:', error);
      this.upcomingTimeOff = [];
      this.completedTimeOff = [];
    }
  }

  /**
   * Categorize entries into upcoming and completed based on their end date
   */
  private categorizeEntries(entries: any[]): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format

    const upcoming: any[] = [];
    const completed: any[] = [];

    for (const entry of entries) {
      // Recurring entries (repeatAnnually) are always considered upcoming
      if (entry.repeatAnnually) {
        upcoming.push(entry);
        continue;
      }

      // US Holidays (relativeDayName) are always upcoming since they recur
      if (entry.relativeDayName) {
        upcoming.push(entry);
        continue;
      }

      // For date-based entries, check if end date (or start date) is in the past
      if (entry.endDate || entry.startDate) {
        const relevantDate = entry.endDate || entry.startDate;
        if (relevantDate < todayStr) {
          completed.push(entry);
        } else {
          upcoming.push(entry);
        }
      } else {
        // No date info, treat as upcoming
        upcoming.push(entry);
      }
    }

    // Sort upcoming by start date (earliest first)
    upcoming.sort((a, b) => {
      const dateA = a.startDate || '';
      const dateB = b.startDate || '';
      return dateA.localeCompare(dateB);
    });

    // Sort completed by end date (most recent first)
    completed.sort((a, b) => {
      const dateA = a.endDate || a.startDate || '';
      const dateB = b.endDate || b.startDate || '';
      return dateB.localeCompare(dateA);
    });

    this.upcomingTimeOff = upcoming;
    this.completedTimeOff = completed;
  }

  /**
   * Group expanded entries by entryID so multi-day entries show as one row
   */
  private groupEntriesByID(entries: any[]): any[] {
    const entryMap = new Map<number, any>();

    for (const entry of entries) {
      const id = Number(entry.entryID);
      if (entryMap.has(id)) {
        // Add day to existing group
        const existing = entryMap.get(id);
        if (entry.dayOfWeek !== null && entry.dayOfWeek !== undefined) {
          if (!existing.daysOfWeek) {
            existing.daysOfWeek = [];
          }
          existing.daysOfWeek.push(Number(entry.dayOfWeek));
        }
      } else {
        // Create new group
        const grouped = { ...entry };
        grouped.entryID = id;
        if (entry.dayOfWeek !== null && entry.dayOfWeek !== undefined) {
          grouped.daysOfWeek = [Number(entry.dayOfWeek)];
        }
        entryMap.set(id, grouped);
      }
    }

    // Sort days within each group and convert to array
    return Array.from(entryMap.values()).map(entry => {
      if (entry.daysOfWeek) {
        entry.daysOfWeek.sort((a: number, b: number) => a - b);
      }
      return entry;
    });
  }

  /**
   * Open modal to add a new time off entry
   */
  async addDayOff(): Promise<void> {
    // Calculate today's date for min date validation
    const today = new Date();
    const minDate = today.toISOString().split('T')[0];

    const modal = await this.modalController.create({
      component: EntryModalComponent,
      componentProps: {
        entry: null,
        scheduleID: this.daysOffScheduleID || 0,
        scheduleType: WorkScheduleType.Days_Off,
        // Allow all entry types for workers - day_of_week uses effectiveDate to control when it starts
        allowedEntryTypes: this.workerEntryTypes,
        // Set minimum date to today to prevent past entries
        minDate: minDate
      }
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'save' && data && Array.isArray(data) && data.length > 0) {
      // Validate that date is not in the past
      // For date-based entries, check startDate; for day_of_week entries, check effectiveDate
      const todayStr = new Date().toISOString().split('T')[0];
      const hasInvalidDate = data.some(entry => {
        // Check startDate for date-based entries
        if (entry.startDate && entry.startDate < todayStr) {
          return true;
        }
        // Check effectiveDate for day_of_week entries
        if (entry.effectiveDate && entry.effectiveDate < todayStr) {
          return true;
        }
        return false;
      });

      if (hasInvalidDate) {
        // Show error - cannot add past dates
        this.popoverService.show({
          title: this.translate.instant('SHARED.Something_Went_Wrong'),
          description: this.translate.instant('WORK_SCHEDULE.Cannot_Add_Past_Time_Off'),
          hideCancelAction: true
        });
        return;
      }

      await this.loadingService.enable();
      try {
        // Add subjectType and subjectID to each entry
        for (const entryData of data) {
          entryData.subjectType = 'worker';
          entryData.subjectID = this.userID;
          entryData.scheduleType = WorkScheduleType.Days_Off;
          await this.workScheduleService.handleAddSubjectEntry(entryData);
        }
        // Reload entries and refresh calendar
        await this.loadWorkerEntries();
        this.refreshCalendar();
      } finally {
        this.loadingService.disable();
      }
    }
  }

  /**
   * Show delete confirmation for an entry
   */
  deleteDayOff(entry: any): void {
    this.entryToDelete = entry;
    this.popoverService.show({
      title: this.translate.instant('WORK_SCHEDULE.Delete_Entry'),
      description: this.translate.instant('WORK_SCHEDULE.Confirm_Delete_Entry'),
      onSave: () => this.confirmDeleteEntry()
    });
  }

  /**
   * Actually delete the entry after confirmation
   */
  private async confirmDeleteEntry(): Promise<void> {
    if (!this.entryToDelete) return;

    await this.loadingService.enable();
    try {
      await this.workScheduleService.handleDeleteSubjectEntry(this.entryToDelete.entryID);
      await this.loadWorkerEntries();
      this.refreshCalendar();
    } finally {
      this.loadingService.disable();
      this.entryToDelete = null;
    }
  }

  /**
   * Refresh the calendar component
   */
  private refreshCalendar(): void {
    if (this.scheduleCalendar) {
      this.scheduleCalendar.loadScheduleData();
    }
  }

  /**
   * Get description text for an entry (main title line)
   */
  getEntryDescription(entry: any): string {
    // Handle grouped entries with multiple days (day of week)
    if (entry.daysOfWeek && entry.daysOfWeek.length > 0) {
      return this.formatDaysDescription(entry.daysOfWeek);
    } else if (entry.dayOfWeek !== null && entry.dayOfWeek !== undefined) {
      return this.translate.instant('WORK_SCHEDULE.' + this.daysOfWeek[entry.dayOfWeek]);
    } else if (entry.startDate) {
      // Date range entry - show the date range as main description
      let dateStr = this.formatDate(entry.startDate);
      if (entry.endDate && entry.endDate !== entry.startDate) {
        dateStr += ' - ' + this.formatDate(entry.endDate);
      }
      if (entry.repeatAnnually) {
        dateStr += ` (${this.translate.instant('WORK_SCHEDULE.Repeat_Annually')})`;
      }
      return dateStr;
    } else if (entry.relativeDayName) {
      const offset = entry.relativeDayOffset ? ` (${entry.relativeDayOffset > 0 ? '+' : ''}${entry.relativeDayOffset})` : '';
      return entry.relativeDayName + offset;
    }
    return '';
  }

  /**
   * Get date range text for day-of-week entries (effectiveDate - expiresDate)
   */
  getEntryDateRange(entry: any): string {
    // Only show date range for day-of-week entries (those with daysOfWeek or dayOfWeek)
    const isDayOfWeekEntry = (entry.daysOfWeek && entry.daysOfWeek.length > 0) ||
                             (entry.dayOfWeek !== null && entry.dayOfWeek !== undefined);
    if (!isDayOfWeekEntry) {
      return '';
    }
    if (!entry.effectiveDate && !entry.expiresDate) {
      return '';
    }

    const effectiveStr = entry.effectiveDate ? this.formatDate(entry.effectiveDate) : '';
    const expiresStr = entry.expiresDate
      ? this.formatDate(entry.expiresDate)
      : this.translate.instant('WORK_SCHEDULE.Forever');

    if (effectiveStr && expiresStr) {
      return `${effectiveStr} - ${expiresStr}`;
    } else if (effectiveStr) {
      return `${this.translate.instant('WORK_SCHEDULE.Starting')} ${effectiveStr}`;
    } else if (entry.expiresDate) {
      return `${this.translate.instant('WORK_SCHEDULE.Until')} ${expiresStr}`;
    }
    return '';
  }

  /**
   * Get time range text for an entry
   */
  getEntryTimeRange(entry: any): string {
    const startTime = entry.startTime;
    const endTime = entry.endTime;

    if (!startTime || !endTime) {
      return '';
    }

    // Check if it's an all-day entry (00:00 - 23:59)
    const normalizeTime = (t: string) => t ? t.substring(0, 5) : '';
    const start = normalizeTime(startTime);
    const end = normalizeTime(endTime);

    if (start === '00:00' && (end === '23:59' || end === '23:59')) {
      return this.translate.instant('WORK_SCHEDULE.All_Day');
    }

    return `${this.formatTime(startTime)} - ${this.formatTime(endTime)}`;
  }

  /**
   * Format time for display (convert 24h to 12h format)
   */
  private formatTime(timeStr: string): string {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${suffix}`;
  }

  /**
   * Check if an entry can be edited (is current or in the future)
   */
  canEditEntry(entry: any): boolean {
    const todayStr = new Date().toISOString().split('T')[0];

    // Recurring entries with repeatAnnually are always editable
    if (entry.repeatAnnually) {
      return true;
    }

    // US Holidays (relativeDayName) are always editable since they recur
    if (entry.relativeDayName) {
      return true;
    }

    // Day-of-week entries: check expiresDate (or effectiveDate if no expires)
    if (entry.daysOfWeek || (entry.dayOfWeek !== null && entry.dayOfWeek !== undefined)) {
      // If no expiresDate, it's forever - editable
      if (!entry.expiresDate) {
        return true;
      }
      // If expiresDate is today or in the future, editable
      return entry.expiresDate >= todayStr;
    }

    // Date-based entries: check if end date (or start date) is today or in the future
    if (entry.endDate || entry.startDate) {
      const relevantDate = entry.endDate || entry.startDate;
      return relevantDate >= todayStr;
    }

    return true;
  }

  /**
   * Open modal to edit an existing time off entry
   */
  async editDayOff(entry: any): Promise<void> {
    const modal = await this.modalController.create({
      component: EntryModalComponent,
      componentProps: {
        entry: entry,
        scheduleID: this.daysOffScheduleID || 0,
        scheduleType: WorkScheduleType.Days_Off,
        allowedEntryTypes: this.workerEntryTypes
      }
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'save' && data && Array.isArray(data) && data.length > 0) {
      await this.loadingService.enable();
      try {
        for (const entryData of data) {
          entryData.subjectType = 'worker';
          entryData.subjectID = this.userID;
          entryData.scheduleType = WorkScheduleType.Days_Off;
          await this.workScheduleService.handleUpdateSubjectEntry(entryData);
        }
        // Reload entries and refresh calendar
        await this.loadWorkerEntries();
        this.refreshCalendar();
      } finally {
        this.loadingService.disable();
      }
    }
  }

  /**
   * Format date for display
   */
  private formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  /**
   * Format array of day numbers into readable description
   */
  private formatDaysDescription(days: number[]): string {
    if (days.length === 0) return '';
    if (days.length === 1) {
      return this.translate.instant('WORK_SCHEDULE.' + this.daysOfWeek[days[0]]);
    }

    // Check for common patterns
    const isWeekdays = days.length === 5 && [1, 2, 3, 4, 5].every(d => days.includes(d));
    const isWeekend = days.length === 2 && days.includes(0) && days.includes(6);
    const isAllDays = days.length === 7;

    if (isAllDays) {
      return this.translate.instant('WORK_SCHEDULE.All_Days');
    }
    if (isWeekdays) {
      return this.translate.instant('WORK_SCHEDULE.Weekdays');
    }
    if (isWeekend) {
      return this.translate.instant('WORK_SCHEDULE.Weekend');
    }

    // Show as list
    return days.map(d => this.daysOfWeekShort[d]).join(', ');
  }

  /**
   * Get notes text for an entry (for display)
   */
  getEntryNotes(entry: any): string {
    return entry.notes || '';
  }
}
